import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';

interface Appointment {
  id: string;
  applicant_name: string;
  email: string;
  phone: string;
  appointment_date: string;
  is_lagos: boolean;
  physical_address: string | null;
  zoom_link: string | null;
  reminder_sent: boolean;
  created_at: string;
  notes: string | null;
  attendance_status: 'pending' | 'attended' | 'no_show' | 'rescheduled';
}

export default function AppointmentsTab() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [savingNotes, setSavingNotes] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Appointment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState<number | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery, selectedDate]);

  const fetchAppointments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('appointments')
      .select('*')
      .order('appointment_date', { ascending: true });

    if (error) {
      console.error('Error fetching appointments:', error);
    } else {
      setAppointments(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error } = await supabase
      .from('appointments')
      .delete()
      .eq('id', deleteTarget.id);

    if (error) {
      console.error('Error deleting appointment:', error);
    } else {
      setAppointments(prev => prev.filter(a => a.id !== deleteTarget.id));
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const { error } = await supabase
      .from('appointments')
      .delete()
      .in('id', Array.from(selectedIds));

    if (error) {
      console.error('Error bulk deleting appointments:', error);
    } else {
      setAppointments(prev => prev.filter(a => !selectedIds.has(a.id)));
      setSelectedIds(new Set());
    }
    setBulkDeleting(false);
    setBulkDeleteTarget(null);
  };

  const updateNotes = async (id: string, notes: string) => {
    setSavingNotes(id);
    const { error } = await supabase
      .from('appointments')
      .update({ notes })
      .eq('id', id);

    if (error) {
      console.error('Error updating notes:', error);
    } else {
      setAppointments(prev =>
        prev.map(app => (app.id === id ? { ...app, notes } : app))
      );
    }
    setSavingNotes(null);
  };

  const updateAttendanceStatus = async (id: string, status: Appointment['attendance_status']) => {
    setSavingStatus(id);
    const { error } = await supabase
      .from('appointments')
      .update({ attendance_status: status })
      .eq('id', id);

    if (error) {
      console.error('Error updating attendance status:', error);
    } else {
      setAppointments(prev =>
        prev.map(app => (app.id === id ? { ...app, attendance_status: status } : app))
      );
    }
    setSavingStatus(null);
  };

  const filteredAppointments = appointments.filter(app => {
    const matchesSearch =
      app.applicant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase());

    if (!selectedDate) return matchesSearch;

    const appDate = new Date(app.appointment_date).toISOString().split('T')[0];
    return matchesSearch && appDate === selectedDate;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAppointments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAppointments.map(a => a.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const isUpcoming = (dateStr: string) => {
    const appDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appDate >= today;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'attended':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'no_show':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'rescheduled':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      default:
        return 'bg-stone-100 text-stone-600 border-stone-300';
    }
  };

  const exportCSV = () => {
    const toExport = selectedIds.size > 0
      ? filteredAppointments.filter(a => selectedIds.has(a.id))
      : filteredAppointments;

    const headers = [
      'Name', 'Email', 'Phone', 'Date', 'Time',
      'Type', 'Venue', 'Attendance', 'Reminder Status', 'Booked Date', 'Notes',
    ];

    const rows = toExport.map(app => [
      app.applicant_name,
      app.email,
      app.phone,
      formatDate(app.appointment_date),
      formatTime(app.appointment_date),
      app.is_lagos ? 'In-Person' : 'Zoom',
      app.is_lagos ? app.physical_address || '' : app.zoom_link || '',
      app.attendance_status || 'pending',
      app.reminder_sent ? 'Sent' : 'Pending',
      new Date(app.created_at).toLocaleDateString(),
      app.notes || '',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `appointments_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDateSummary = () => {
    if (!selectedDate) return null;
    const count = filteredAppointments.length;
    const dateObj = new Date(selectedDate + 'T00:00:00');
    const formatted = dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    return `${count} appointment${count !== 1 ? 's' : ''} on ${formatted}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Single Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-base">Delete Appointment</h3>
                <p className="text-sm text-stone-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-stone-700">
              Are you sure you want to delete the appointment for{' '}
              <strong>{deleteTarget.applicant_name}</strong> scheduled on{' '}
              <strong>{formatDate(deleteTarget.appointment_date)}</strong>?
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {deleting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Deleting...</>
                ) : (
                  <><i className="ri-delete-bin-line"></i> Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteTarget !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-base">Delete Multiple Appointments</h3>
                <p className="text-sm text-stone-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-stone-700">
              Are you sure you want to delete <strong>{bulkDeleteTarget} appointment{bulkDeleteTarget !== 1 ? 's' : ''}</strong>?
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setBulkDeleteTarget(null)}
                disabled={bulkDeleting}
                className="flex-1 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {bulkDeleting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Deleting...</>
                ) : (
                  <><i className="ri-delete-bin-line"></i> Delete All</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-emerald-600 text-white rounded-lg px-4 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <i className="ri-checkbox-multiple-line text-xl"></i>
            <span className="font-medium">{selectedIds.size} appointment{selectedIds.size !== 1 ? 's' : ''} selected</span>
          </div>
          <button
            onClick={() => setBulkDeleteTarget(selectedIds.size)}
            className="px-4 py-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-delete-bin-line"></i> Delete Selected
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
          />
          {selectedDate && (
            <button
              onClick={() => setSelectedDate('')}
              className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              Clear Date
            </button>
          )}
        </div>
        <button
          onClick={exportCSV}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
        >
          {selectedIds.size > 0 ? `Export Selected (${selectedIds.size})` : 'Export CSV'}
        </button>
      </div>

      {/* Date Summary */}
      {getDateSummary() && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
          <p className="text-emerald-800 font-medium text-sm">{getDateSummary()}</p>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto bg-white rounded-lg border border-stone-200">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={filteredAppointments.length > 0 && selectedIds.size === filteredAppointments.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Phone</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Venue</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Attendance</th>
              <th className="px-6 py-3 text-left text-xs font-bold text-emerald-700 uppercase tracking-wider whitespace-nowrap">Reminders</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Booked</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-stone-700 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {filteredAppointments.map(app => (
              <>
                <tr
                  key={app.id}
                  className={`hover:bg-stone-50 transition-colors ${
                    isUpcoming(app.appointment_date) ? 'border-l-4 border-l-emerald-500' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(app.id)}
                      onChange={() => toggleSelect(app.id)}
                      className="w-4 h-4 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-900">{app.applicant_name}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{app.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <a
                      href={`https://wa.me/${app.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:text-emerald-700 hover:underline"
                    >
                      {app.phone}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-900">{formatDate(app.appointment_date)}</td>
                  <td className="px-4 py-3 text-sm text-stone-900">{formatTime(app.appointment_date)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      app.is_lagos ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-800'
                    }`}>
                      {app.is_lagos ? 'In-Person' : 'Zoom'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {app.is_lagos ? (
                      <span className="line-clamp-2">{app.physical_address}</span>
                    ) : (
                      <a
                        href={app.zoom_link || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 hover:underline"
                      >
                        Join Zoom
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={app.attendance_status || 'pending'}
                      onChange={e => updateAttendanceStatus(app.id, e.target.value as Appointment['attendance_status'])}
                      disabled={savingStatus === app.id}
                      className={`text-xs font-semibold px-2 py-1 rounded-lg border cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${getStatusStyle(app.attendance_status || 'pending')} ${savingStatus === app.id ? 'opacity-50' : ''}`}
                    >
                      <option value="pending">⏳ Pending</option>
                      <option value="attended">✅ Attended</option>
                      <option value="no_show">❌ No-Show</option>
                      <option value="rescheduled">🔄 Rescheduled</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        (app as any).reminder_24hr_sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {(app as any).reminder_24hr_sent ? <><i className="ri-check-line text-xs"></i> 24hr Sent</> : <><i className="ri-time-line text-xs"></i> 24hr Pending</>}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        (app as any).reminder_evening_sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {(app as any).reminder_evening_sent ? <><i className="ri-check-line text-xs"></i> Evening Sent</> : <><i className="ri-time-line text-xs"></i> Evening Pending</>}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        (app as any).reminder_2hr_sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {(app as any).reminder_2hr_sent ? <><i className="ri-check-line text-xs"></i> 2hr Sent</> : <><i className="ri-time-line text-xs"></i> 2hr Pending</>}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {new Date(app.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => setDeleteTarget(app)}
                      className="flex items-center gap-1 text-red-500 hover:text-red-700 font-medium text-xs cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line"></i> Delete
                    </button>
                  </td>
                </tr>
                <tr key={`${app.id}-notes`}>
                  <td colSpan={12} className="px-4 py-3 bg-stone-50">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-stone-700 uppercase tracking-wider">
                        Notes / Comments
                      </label>
                      <textarea
                        value={app.notes || ''}
                        onChange={e =>
                          setAppointments(prev =>
                            prev.map(a => (a.id === app.id ? { ...a, notes: e.target.value } : a))
                          )
                        }
                        onBlur={e => updateNotes(app.id, e.target.value)}
                        placeholder="Add notes about this appointment..."
                        rows={2}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                        disabled={savingNotes === app.id}
                      />
                      {savingNotes === app.id && (
                        <p className="text-xs text-emerald-600">Saving...</p>
                      )}
                    </div>
                  </td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
        {filteredAppointments.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            No appointments found matching your filters.
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredAppointments.map(app => (
          <div
            key={app.id}
            className={`bg-white rounded-lg border border-stone-200 p-4 space-y-3 ${
              isUpcoming(app.appointment_date) ? 'border-l-4 border-l-emerald-500' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedIds.has(app.id)}
                onChange={() => toggleSelect(app.id)}
                className="w-5 h-5 text-emerald-600 border-stone-300 rounded focus:ring-emerald-500 cursor-pointer mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-stone-900">{app.applicant_name}</h3>
                    <p className="text-sm text-stone-600">{app.email}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    app.is_lagos ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-800'
                  }`}>
                    {app.is_lagos ? 'In-Person' : 'Zoom'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-stone-400"></i>
                <a
                  href={`https://wa.me/${app.phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  {app.phone}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-stone-400"></i>
                <span className="text-stone-900">{formatDate(app.appointment_date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-stone-400"></i>
                <span className="text-stone-900">{formatTime(app.appointment_date)}</span>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-map-pin-line text-stone-400 mt-0.5"></i>
                {app.is_lagos ? (
                  <span className="text-stone-600">{app.physical_address}</span>
                ) : (
                  <a
                    href={app.zoom_link || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 hover:underline"
                  >
                    Join Zoom Meeting
                  </a>
                )}
              </div>

              {/* Attendance Status */}
              <div className="flex items-center gap-2">
                <i className="ri-user-follow-line text-stone-400"></i>
                <select
                  value={app.attendance_status || 'pending'}
                  onChange={e => updateAttendanceStatus(app.id, e.target.value as Appointment['attendance_status'])}
                  disabled={savingStatus === app.id}
                  className={`text-xs font-semibold px-2 py-1 rounded-lg border cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${getStatusStyle(app.attendance_status || 'pending')} ${savingStatus === app.id ? 'opacity-50' : ''}`}
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="attended">✅ Attended</option>
                  <option value="no_show">❌ No-Show</option>
                  <option value="rescheduled">🔄 Rescheduled</option>
                </select>
                {savingStatus === app.id && <span className="text-xs text-emerald-600">Saving...</span>}
              </div>

              <div className="pb-2 border-b border-stone-200">
                <p className="text-xs text-stone-500 mb-1">Reminders</p>
                <div className="flex flex-col gap-1">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap w-fit ${
                    (app as any).reminder_24hr_sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {(app as any).reminder_24hr_sent ? <><i className="ri-check-line text-xs"></i> 24hr Sent</> : <><i className="ri-time-line text-xs"></i> 24hr Pending</>}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap w-fit ${
                    (app as any).reminder_evening_sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {(app as any).reminder_evening_sent ? <><i className="ri-check-line text-xs"></i> Evening Sent</> : <><i className="ri-time-line text-xs"></i> Evening Pending</>}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap w-fit ${
                    (app as any).reminder_2hr_sent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {(app as any).reminder_2hr_sent ? <><i className="ri-check-line text-xs"></i> 2hr Sent</> : <><i className="ri-time-line text-xs"></i> 2hr Pending</>}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <i className="ri-calendar-check-line text-stone-400"></i>
                <span className="text-stone-600">Booked: {new Date(app.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="pt-3 border-t border-stone-200 space-y-2">
              <label className="text-xs font-semibold text-stone-700 uppercase tracking-wider">
                Notes / Comments
              </label>
              <textarea
                value={app.notes || ''}
                onChange={e =>
                  setAppointments(prev =>
                    prev.map(a => (a.id === app.id ? { ...a, notes: e.target.value } : a))
                  )
                }
                onBlur={e => updateNotes(app.id, e.target.value)}
                placeholder="Add notes about this appointment..."
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                disabled={savingNotes === app.id}
              />
              {savingNotes === app.id && (
                <p className="text-xs text-emerald-600">Saving...</p>
              )}
            </div>

            <button
              onClick={() => setDeleteTarget(app)}
              className="w-full flex items-center justify-center gap-2 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors cursor-pointer"
            >
              <i className="ri-delete-bin-line"></i> Delete Appointment
            </button>
          </div>
        ))}
        {filteredAppointments.length === 0 && (
          <div className="text-center py-12 text-stone-500 bg-white rounded-lg border border-stone-200">
            No appointments found matching your filters.
          </div>
        )}
      </div>
    </div>
  );
}
